import { LucideIcon, CheckCircle } from "lucide-react";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";

interface AICapabilityItem {
  title: string;
  description: string;
  icon: LucideIcon;
  useCases: string[];
}

interface AICapabilitiesSectionProps {
  badge: {
    icon: LucideIcon;
    text: string;
  };
  title: string;
  titleHighlight: string;
  subtitle: string;
  items: AICapabilityItem[];
  accentColor?: string;
}

export const AICapabilitiesSection = ({
  badge,
  title,
  titleHighlight,
  subtitle,
  items,
  accentColor = "purple",
}: AICapabilitiesSectionProps) => {
  const BadgeIcon = badge.icon;

  const colorClasses: Record<string, { badge: string; title: string; icon: string; border: string; check: string }> = {
    purple: {
      badge: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      title: "text-purple-600 dark:text-purple-400",
      icon: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      border: "hover:border-purple-500/30",
      check: "text-purple-500",
    },
    blue: {
      badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      title: "text-blue-600 dark:text-blue-400",
      icon: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      border: "hover:border-blue-500/30",
      check: "text-blue-500",
    },
  };

  const colors = colorClasses[accentColor] || colorClasses.purple;

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${colors.badge}`}>
              <BadgeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
              {title} <span className={colors.title}>{titleHighlight}</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">{subtitle}</p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border transition-all duration-300 ${colors.border}`}>
                  <div className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 ${colors.icon}`}>
                    <ItemIcon className="w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6 md:h-6" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground mb-1.5 sm:mb-2">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{item.description}</p>
                  <div className="space-y-1.5 sm:space-y-2">
                    {item.useCases.map((useCase, i) => (
                      <div key={i} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                        <CheckCircle className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${colors.check}`} />
                        {useCase}
                      </div>
                    ))}
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};
